import type { QueryClient } from "@tanstack/react-query";
import { create } from "zustand";

import * as mineApi from "../api";
import { uploadToSignedUrl } from "../hooks";
import { mineQueryKeys } from "../query-options";
import type { BookmarkImportTask } from "../types";

const BOOKMARK_IMPORT_MIME_TYPE = "text/html";
const POLL_INTERVAL_MS = 1200;
const UPLOAD_PROGRESS_MAX = 20;
const PROCESSING_PROGRESS_FLOOR = 24;
const TERMINAL_STATUSES = new Set<BookmarkImportTask["status"]>(["succeeded", "failed"]);

export type BookmarkImportPhase =
  | "idle"
  | "creating"
  | "uploading"
  | "confirming"
  | "processing"
  | "succeeded"
  | "failed";

interface BookmarkImportProgressState {
  active: boolean;
  visible: boolean;
  fileName: string;
  phase: BookmarkImportPhase;
  progress: number;
  task: BookmarkImportTask | null;
  errorMessage: string;
  actions: {
    startImport: (file: File, queryClient: QueryClient) => void;
    dismiss: () => void;
  };
}

function wait(ms: number) {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

function getBackendProgress(task: BookmarkImportTask) {
  if (task.status === "succeeded") {
    return 100;
  }

  return Math.max(0, Math.min(100, task.progress_percent || 0));
}

function setDisplayProgress(
  set: (
    partial:
      | Partial<BookmarkImportProgressState>
      | ((state: BookmarkImportProgressState) => Partial<BookmarkImportProgressState>),
  ) => void,
  nextProgress: number,
) {
  const normalizedProgress = Math.max(0, Math.min(100, nextProgress));

  set((state) => ({
    progress: Math.max(state.progress, normalizedProgress),
  }));
}

async function pollTask(
  taskId: string,
  set: (
    partial:
      | Partial<BookmarkImportProgressState>
      | ((state: BookmarkImportProgressState) => Partial<BookmarkImportProgressState>),
  ) => void,
) {
  let latestTask = await mineApi.getBookmarkImportTask(taskId);
  set({ task: latestTask });
  setDisplayProgress(set, getBackendProgress(latestTask));

  while (!TERMINAL_STATUSES.has(latestTask.status)) {
    await wait(POLL_INTERVAL_MS);
    latestTask = await mineApi.getBookmarkImportTask(taskId);
    set({ task: latestTask });
    setDisplayProgress(set, getBackendProgress(latestTask));
  }

  return latestTask;
}

export const useBookmarkImportProgressStore = create<BookmarkImportProgressState>()((set) => ({
  active: false,
  visible: false,
  fileName: "",
  phase: "idle",
  progress: 0,
  task: null,
  errorMessage: "",
  actions: {
    startImport: (file, queryClient) => {
      set({
        active: true,
        visible: true,
        fileName: file.name,
        phase: "creating",
        progress: 2,
        task: null,
        errorMessage: "",
      });

      void (async () => {
        try {
          const createdTask = await mineApi.createBookmarkImportTask({
            file_name: file.name,
            mime_type: BOOKMARK_IMPORT_MIME_TYPE,
            size: String(file.size),
          });
          set({ task: createdTask });
          setDisplayProgress(set, 5);

          set({ phase: "uploading" });
          await uploadToSignedUrl(
            createdTask.upload_url,
            file,
            BOOKMARK_IMPORT_MIME_TYPE,
            (nextUploadProgress) => {
              setDisplayProgress(set, (nextUploadProgress / 100) * UPLOAD_PROGRESS_MAX);
            },
          );
          setDisplayProgress(set, UPLOAD_PROGRESS_MAX);

          set({ phase: "confirming" });
          const confirmedTask = await mineApi.confirmBookmarkImportUpload({ id: createdTask.id });
          set({ task: confirmedTask });
          setDisplayProgress(set, PROCESSING_PROGRESS_FLOOR);

          set({ phase: "processing" });
          const finalTask = await pollTask(createdTask.id, set);

          if (finalTask.status === "failed") {
            set({
              visible: true,
              phase: "failed",
              errorMessage: finalTask.error_message || "书签导入失败，请检查文件后重试。",
            });
            return;
          }

          set({ visible: true, phase: "succeeded", progress: 100 });
          void queryClient.invalidateQueries({ queryKey: mineQueryKeys.items });
          void queryClient.invalidateQueries({ queryKey: mineQueryKeys.folders });
        } catch {
          set({
            visible: true,
            phase: "failed",
            errorMessage: "书签导入失败，请稍后重试。",
          });
        }
      })();
    },
    dismiss: () => {
      set((state) => {
        if (state.phase !== "succeeded" && state.phase !== "failed") {
          return { visible: false };
        }

        return {
          active: false,
          visible: false,
          fileName: "",
          phase: "idle",
          progress: 0,
          task: null,
          errorMessage: "",
        };
      });
    },
  },
}));

export const useBookmarkImportProgressActions = () =>
  useBookmarkImportProgressStore((state) => state.actions);
